// WHAT: Page component for overview view with workspace/aspect statistics
// WHY:  Provides interface for viewing worked vs planned hours by workspace or aspect
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: OverviewTab, useSupabaseTasks, useWorkspaceConfig, core.invoke
// RISKS:
//   - NOTE: Overview data calculation depends on backend - may fail silently
//   - NOTE: Fallback logic uses task estimates - may not be accurate
//   - NOTE: Color mapping depends on config structure - may fail for missing configs

import { core } from "@tauri-apps/api";
import React, { useState, useEffect } from "react";

import { useSupabaseTasks } from "../../hooks/data/useSupabasetasks";
import { useWorkspaceConfig } from "../../hooks/time_tracking/useWorkspaceConfig";
import { OverviewTab } from "../../screen/tabs/OverviewTab";

/**
 * Overview entry interface
 */
export interface OverviewEntry {
  /** Display color */
  color: string;
  /** Entry name (workspace or aspect) */
  name: string;
  /** Hours planned */
  planned: number;
  /** Hours worked */
  worked: number;
}

/**
 * WHAT:
 *   Creates overview from tasks as fallback when shortfalls are unavailable.
 *
 * @param tasks - Task list
 *
 * @param config - Workspace config
 *
 * @param timeFilter - Time filter mode
 *
 * @returns Workspace and aspect overview data
 *
 */
const createOverviewFromTasks = (
  tasks: ReturnType<typeof useSupabaseTasks>["tasks"],
  config: ReturnType<typeof useWorkspaceConfig>[0],
  timeFilter: "monthly" | "today",
): { aspectOverview: OverviewEntry[]; workspaceOverview: OverviewEntry[] } => {
  const taskMap = new Map<string, { worked: number; planned: number }>();
  const aspData: OverviewEntry[] = [];

  for (const task of tasks) {
    if (task.status === "Done") {
      continue;
    }

    const workspace = task.workspace ?? "Unknown";
    const planned =
      timeFilter === "today" ? (task.estimated_time ?? 1) / 30 : (task.estimated_time ?? 1);
    const worked = 0;

    if (!taskMap.has(workspace)) {
      taskMap.set(workspace, { planned: 0, worked: 0 });
    }
    const ws = taskMap.get(workspace);
    if (ws) {
      ws.worked += worked;
      ws.planned += planned;
    }

    const aspect = task.aspect ?? "Unknown";
    const aspColor =
      getSafeAspectColor(
        config as Record<string, Record<string, { color: string; hours: number }>>,
        workspace,
        aspect,
      ) ?? "#64748b";
    aspData.push({
      color: aspColor,
      name: `${workspace} / ${aspect}`,
      planned: planned,
      worked: worked,
    });
  }

  const wsData: OverviewEntry[] = [...taskMap.entries()].map(([name, data]) => ({
    color: getWorkspaceColor(
      config as Record<string, Record<string, { color: string; hours: number }>>,
      name,
    ),
    name,
    planned: data.planned,
    worked: data.worked,
  }));

  return { aspectOverview: aspData, workspaceOverview: wsData };
};

/**
 * WHAT:
 *   Processes shortfalls data to create overview.
 *
 * @param shortfalls - Shortfalls data
 *
 * @param config - Workspace config
 *
 * @param timeFilter - Time filter mode
 *
 * @returns Workspace and aspect overview data
 *
 */
const processShortfallsData = (
  shortfalls: {
    workspace: string;
    aspect: string;
    total_hours_worked?: unknown;
    planned_hours_daily?: unknown;
    planned_hours_monthly?: unknown;
  }[],
  config: ReturnType<typeof useWorkspaceConfig>[0],
  timeFilter: "monthly" | "today",
): { aspectOverview: OverviewEntry[]; workspaceOverview: OverviewEntry[] } => {
  const wsMap = new Map<string, { worked: number; planned: number }>();
  const aspData: OverviewEntry[] = [];

  for (const item of shortfalls) {
    const workspace = item.workspace;
    const aspect = item.aspect;
    const worked = typeof item.total_hours_worked === "number" ? item.total_hours_worked : 0;
    const plannedDaily =
      typeof item.planned_hours_daily === "number" ? item.planned_hours_daily : 0;
    const plannedMonthly =
      typeof item.planned_hours_monthly === "number" ? item.planned_hours_monthly : 0;
    const planned = timeFilter === "today" ? plannedDaily || plannedMonthly / 30 : plannedMonthly;

    if (!wsMap.has(workspace)) {
      wsMap.set(workspace, { planned: 0, worked: 0 });
    }
    const ws = wsMap.get(workspace);
    if (ws) {
      ws.worked += worked;
      ws.planned += planned;
    }

    const aspColor =
      getSafeAspectColor(
        config as Record<string, Record<string, { color: string; hours: number }>>,
        workspace,
        aspect,
      ) ?? "#64748b";
    aspData.push({
      color: aspColor,
      name: `${workspace} / ${aspect}`,
      planned: planned,
      worked: worked,
    });
  }

  const wsData: OverviewEntry[] = [...wsMap.entries()].map(([name, data]) => ({
    color: getWorkspaceColor(
      config as Record<string, Record<string, { color: string; hours: number }>>,
      name,
    ),
    name,
    planned: data.planned,
    worked: data.worked,
  }));

  return { aspectOverview: aspData, workspaceOverview: wsData };
};

/**
 * WHAT:
 *   Page component that displays overview statistics for workspaces and aspects
 *   showing worked vs planned hours with filtering options.
 *
 * WHY:
 *   Enables users to quickly see overall progress across workspaces/aspects
 *   and identify areas needing attention.
 *
 * BEHAVIOR:
 *   - Loads tasks and workspace configuration
 *   - Calculates overview data from shortfalls
 *   - Supports workspace and aspect view modes
 *   - Supports today and monthly time filters
 *   - Falls back to task estimates if shortfalls unavailable
 *   - Displays data in OverviewTab component
 *
 * RISKS:
 *   - NOTE: Overview data calculation depends on backend - may fail silently
 *   - NOTE: Fallback logic uses task estimates - may not be accurate
 *   - NOTE: Color mapping depends on config structure - may fail for missing configs
 *
 * @returns {JSX.Element} The overview page component
 *
 */
export const OverviewPage: React.FC = () => {
  const { tasks } = useSupabaseTasks();
  const [config] = useWorkspaceConfig();
  const [overviewMode, setOverviewMode] = useState<"aspect" | "workspace">("workspace");
  const [timeFilter, setTimeFilter] = useState<"monthly" | "today">("monthly");
  const [workspaceOverview, setWorkspaceOverview] = useState<OverviewEntry[]>([]);
  const [aspectOverview, setAspectOverview] = useState<OverviewEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOverviewData = async () => {
      if (tasks.length === 0 || Object.keys(config).length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        const raw = await core.invoke<{
          shortfalls: {
            parent: string;
            child: string;
            [key: string]: unknown;
          }[];
          worked_today: number;
        }>("calculate_shortfalls", { appHandle: {} });

        if (raw.shortfalls.length === 0) {
          // NOTE: Fallback: Create overview from tasks directly (less accurate)
          const { aspectOverview, workspaceOverview } = createOverviewFromTasks(
            tasks,
            config,
            timeFilter,
          );
          setWorkspaceOverview(workspaceOverview);
          setAspectOverview(aspectOverview);
          return;
        }

        // NOTE: Process shortfalls data for overview
        const { aspectOverview, workspaceOverview } = processShortfallsData(
          raw.shortfalls.map((item) => ({
            aspect: item.child || "",
            planned_hours_daily: item.plannedUntilToday,
            planned_hours_monthly: item.planned,
            total_hours_worked: item.worked,
            workspace: item.parent || "",
          })),
          config as unknown as ReturnType<typeof useWorkspaceConfig>[0],
          timeFilter,
        );

        setWorkspaceOverview(workspaceOverview);
        setAspectOverview(aspectOverview);
      } catch {
        setWorkspaceOverview([]);
        setAspectOverview([]);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchOverviewData();
  }, [tasks, config, timeFilter]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-[#94A3B8]">Lade Übersicht...</p>
        </div>
      </div>
    );
  }

  return (
    <OverviewTab
      aspectOverview={aspectOverview}
      dataLoading={isLoading}
      overviewMode={overviewMode}
      setOverviewMode={setOverviewMode}
      setTimeFilter={setTimeFilter}
      timeFilter={timeFilter}
      workspaceOverview={workspaceOverview}
    />
  );
};

/**
 * WHAT:
 *   Returns color for workspace from config.
 *
 * WHY:
 *   Provides consistent color mapping for workspace visualization.
 *
 * BEHAVIOR:
 *   - Gets first aspect color as workspace color
 *   - Returns default gray if workspace not found
 *
 * @param config
 *
 * @param workspace
 *
 * @returns {string} Color hex code for the workspace
 *
 */
function getWorkspaceColor(
  config: Record<string, Record<string, { color: string; hours: number }>>,
  workspace: string,
): string {
  const workspaceConfig = config[workspace];
  if (!workspaceConfig) {
    return "#64748b";
  }

  // NOTE: Get the first aspect color as workspace color
  const firstAspect = Object.values(workspaceConfig)[0] as undefined | { color?: string };
  return firstAspect?.color ?? "#64748b";
}

/**
 * WHAT:
 *   Safely retrieves aspect color from config.
 *
 * WHY:
 *   Provides safe color access with error handling.
 *
 * BEHAVIOR:
 *   - Returns aspect color if found
 *   - Returns undefined if workspace/aspect not found
 *   - Handles errors gracefully
 *
 * @param config
 *
 * @param workspace
 *
 * @param aspect
 *
 * @returns Color string or undefined
 *
 */
function getSafeAspectColor(
  config: Record<string, Record<string, { color: string; hours: number }>>,
  workspace: string,
  aspect: string,
): string | undefined {
  try {
    const workspaceConfig = config[workspace];
    if (!workspaceConfig) {
      return undefined;
    }

    const aspectConfig = workspaceConfig[aspect];
    if (!aspectConfig) {
      return undefined;
    }

    return aspectConfig.color;
  } catch {
    return undefined;
  }
}
